// Formats an insurance/licence coverage period into a 3-part display payload
// (range + meta + tone) for the InsuranceTab's Coverage period column. Tone
// is reminderDays-driven, with a 30-day floor for the expiring window.

import { getBrisbaneToday } from "./getBrisbaneToday";
import { formatDayMonthYear } from "./formatShortDate";
import { daysBetween } from "./isoDay";

export type CoverageTone =
  | "muted"
  | "success"
  | "warning"
  | "brand"
  | "danger"
  | "neutral";

export interface FormattedCoverage {
  range: string;
  meta: string;
  tone: CoverageTone;
}

export interface FormatCoverageInput {
  startDate: string;
  expiryDate: string;
  reminderDays?: number[];
  isHistorical?: boolean;
  today?: string;
}

// Natural-phrasing duration for the meta line: days for
// 0–30, weeks for 30–90, months for 90 days–24 months, years for 2y+.
// Keeps the at-a-glance scan idiomatic ("3 years" not "42 months") so
// admins can act without expanding. The years branch shows whole years
// when the leftover months are 0, otherwise "Yy Mm" so a long-running
// approved variation reads "approved 3y 6m ago" rather than "approved
// 42 months ago".
export function humaniseDays(days: number): string {
  if (days <= 30) return `${days}d`;
  if (days <= 90) return `${Math.round(days / 7)} weeks`;
  const months = Math.max(1, Math.round(days / 30));
  if (months < 24) {
    return `${months} month${months === 1 ? "" : "s"}`;
  }
  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  if (remMonths === 0) {
    return `${years} year${years === 1 ? "" : "s"}`;
  }
  return `${years}y ${remMonths}m`;
}

export function formatCoverage({
  startDate,
  expiryDate,
  reminderDays,
  isHistorical = false,
  today = getBrisbaneToday(),
}: FormatCoverageInput): FormattedCoverage {
  const range = `${formatDayMonthYear(startDate)} – ${formatDayMonthYear(expiryDate)}`;

  // Expired wins over future-start: a row whose expiry is in the past is
  // expired regardless of whether the start was nominally future. The
  // historical flag demotes the tone but keeps the message.
  if (expiryDate < today) {
    const daysAgo = daysBetween(expiryDate, today);
    return {
      range,
      meta: `Expired ${humaniseDays(daysAgo)} ago`,
      tone: isHistorical ? "muted" : "danger",
    };
  }

  if (startDate > today) {
    const daysToStart = daysBetween(today, startDate);
    return {
      range,
      meta: `Starts in ${humaniseDays(daysToStart)}`,
      tone: "neutral",
    };
  }

  const threshold = Math.max(...(reminderDays ?? []), 30);
  const daysToExpiry = daysBetween(today, expiryDate);

  return {
    range,
    // Within the reminder window → amber, matching the insurance
    // traffic-light vocabulary (current=green → expiring=amber →
    // expired=red). Outside the window → success (emerald).
    meta: `Expires in ${humaniseDays(daysToExpiry)}`,
    tone: daysToExpiry <= threshold ? "warning" : "success",
  };
}
